import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { File } from "lucide-react";
import { Folder } from "lucide-react";
import { LayoutDashboard } from "lucide-react";
import { Plug2 } from "lucide-react";
import { Unlink } from "lucide-react";
import { User } from "lucide-react";
import { Workflow } from "lucide-react";
import { slowFadeUp, slowStagger } from "../utils/animations";
import IconBox from "./IconBox";
import { iconBoxFlexClass } from "../utils/iconBoxClasses";
import FragmentedSystemsPreview from "./FragmentedSystemsPreview";
import limitedVisibilityDashboardUrl from "../assets/limited-visibility-dashboard.svg";
import manualProcessStacksUrl from "../assets/manual-processes-stacks.svg";
import poorPatientExperienceUrl from "../assets/poor-patient-experience.svg";

export default function ProblemSolution() {
  const challengeCards = [
    {
      title: "Fragmented Systems",
      description:
        "Multiple tools for billing, EMR, and operations create inefficiencies.",
      accent: "bg-rose-50 text-rose-500",
      icon: "broken-link",
    },
    {
      title: "Manual Processes",
      description:
        "Paper-based workflows increase errors and cause delays across teams.",
      accent: "bg-rose-50 text-rose-500",
      icon: "file",
    },
    {
      title: "Limited Visibility",
      description:
        "Lack of real-time insights impacts decision-making and productivity.",
      accent: "bg-rose-50 text-rose-500",
      icon: "signal-bars",
    },
    {
      title: "Poor Patient Experience",
      description:
        "Long wait times and disconnected journeys frustrate patients.",
      accent: "bg-rose-50 text-rose-500",
      icon: "user",
    },
  ];

  const solutionCards = [
    {
      id: "centralized-patient-records",
      lines: ["Centralized", "Patient Records"],
      points: ["Complete patient history", "Secure data access", "Anytime, anywhere"],
      accent: "bg-emerald-50 text-emerald-600",
      icon: "file-user",
    },
    {
      id: "automated-workflows",
      lines: ["Automated", "Workflows"],
      points: ["Reduce manual work", "Minimize errors", "Improve efficiency"],
      accent: "bg-sky-50 text-sky-600",
      icon: "workflow",
    },
    {
      id: "real-time-dashboards",
      lines: ["Real-time", "Dashboards"],
      points: ["Live insights", "Track performance", "Data-driven decisions"],
      accent: "bg-violet-50 text-violet-600",
      icon: "dashboard",
    },
    {
      id: "seamless-integrations",
      lines: ["Seamless", "Integrations"],
      points: ["Lab, pharmacy, insurance", "Third-party connections", "Unified ecosystem"],
      accent: "bg-cyan-50 text-cyan-600",
      icon: "plug",
    },
  ];

  const renderChallengeIcon = (card) => {
    switch (card.icon) {
      case "broken-link":
        return <Unlink className="size-6" strokeWidth={1.75} aria-hidden />;
      case "file":
        return <File className="size-6" strokeWidth={1.75} aria-hidden />;
      case "signal-bars":
        return (
          <span className="flex h-6 items-end justify-center gap-0.5" aria-hidden>
            <span className="h-2 w-1.5 rounded-full bg-current" />
            <span className="h-3 w-1.5 rounded-full bg-current" />
            <span className="h-4 w-1.5 rounded-full bg-current" />
          </span>
        );
      case "user":
        return <User className="size-6" strokeWidth={1.75} aria-hidden />;
      default:
        return card.icon;
    }
  };

  const renderSolutionIcon = (item) => {
    if (item.icon === "file-user") {
      return (
        <span className="relative flex size-6 items-center justify-center text-current">
          <Folder className="size-6" strokeWidth={1.75} aria-hidden />
          <User
            className="absolute left-1/2 top-[52%] size-3 -translate-x-1/2 -translate-y-1/2"
            strokeWidth={2.5}
            aria-hidden
          />
        </span>
      );
    }
    const SolutionIcon =
      item.icon === "workflow"
        ? Workflow
        : item.icon === "dashboard"
          ? LayoutDashboard
          : Plug2;
    return <SolutionIcon className="size-6" strokeWidth={1.75} aria-hidden />;
  };

  const renderChallengePreview = (title) => {
    if (title === "Fragmented Systems") {
      return (
        <div className="mt-auto pt-5 h-40 overflow-hidden rounded-xl">
          <FragmentedSystemsPreview className="object-contain object-center" />
        </div>
      );
    }

    if (title === "Manual Processes") {
      return (
        <div className="mt-auto pt-5 h-40 overflow-hidden rounded-xl">
          <img
            src={manualProcessStacksUrl}
            alt="Isometric stacks of paperwork with a red error mark"
            className="h-full w-full object-contain object-center"
            decoding="async"
          />
        </div>
      );
    }

    if (title === "Limited Visibility") {
      return (
        <div className="mt-auto pt-5 h-40 overflow-hidden rounded-xl">
          <img
            src={limitedVisibilityDashboardUrl}
            alt="Dashboard charts with blocked data indicator"
            className="h-full w-full object-contain object-center"
            decoding="async"
          />
        </div>
      );
    }

    if (title === "Poor Patient Experience") {
      return (
        <div className="mt-auto pt-5 h-40 overflow-hidden rounded-xl">
          <img
            src={poorPatientExperienceUrl}
            alt="Patient queue with wait time and one highlighted urgent case"
            className="h-full w-full object-contain object-center"
            decoding="async"
          />
        </div>
      );
    }

    return <div className="mt-auto pt-5 h-40 rounded-xl bg-slate-100" />;
  };

  return (
    <section
      id="solutions"
      className="section-viewport scroll-mt-24 py-12 md:py-20 px-4 md:px-6 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 text-center md:mb-14">
          <span className="inline-flex items-center rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold tracking-wide text-rose-600">
            <span className="relative mr-2 inline-flex h-5 w-5 items-center justify-center">
              <span className="soft-ping absolute inline-flex h-5 w-5 rounded-full bg-red-400 opacity-60" />
              <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[11px] font-bold text-white">
                !
              </span>
            </span>
            The Challenge
          </span>
          <h2 className="mx-auto mt-4 max-w-3xl text-section font-bold text-secondary leading-tight [text-wrap:balance]">
            Hospitals Struggle
            <br className="hidden md:block" /> with{" "}
            <span className="bg-[linear-gradient(90deg,rgba(242,95,95,1)_7%,rgba(227,13,59,1)_51%)] bg-clip-text text-transparent">
              Disconnected Systems
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted">
            Manual workflows, fragmented tools, and data silos slow down care
            delivery and increase operational complexity.
          </p>
        </div>

        <motion.div
          variants={slowStagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 items-stretch"
        >
          {challengeCards.map((card, index) => {
            const dividerClass = [
              index > 0 && "border-t border-slate-200",
              index > 0 && index < 2 && "sm:border-t-0",
              index % 2 === 1 && "sm:border-l",
              index > 0 && "lg:border-l lg:border-t-0",
            ]
              .filter(Boolean)
              .join(" ");

            return (
            <motion.article
              variants={slowFadeUp}
              key={card.title}
              className={`relative flex h-full min-h-0 flex-col p-5 sm:p-6 lg:px-8 ${dividerClass}`}
            >
              <IconBox size="xl" tone={card.accent} className="mb-4 shrink-0">
                {renderChallengeIcon(card)}
              </IconBox>
              <h3 className="text-lg font-semibold text-secondary">{card.title}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted">{card.description}</p>
              {renderChallengePreview(card.title)}
            </motion.article>
            );
          })}
        </motion.div>

        <div className="my-10 md:my-12 flex items-center gap-4">
          <div className="h-px flex-1 border-t border-dashed border-slate-300" />
          <div
            className={iconBoxFlexClass(
              "md",
              "border border-emerald-200 bg-emerald-50 text-emerald-600 shadow-none",
            )}
          >
            <Check className="h-5 w-5" />
          </div>
          <div className="h-px flex-1 border-t border-dashed border-slate-300" />
        </div>

        <motion.div
          variants={slowStagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="flex flex-col gap-6 md:gap-8 items-stretch"
        >
          <motion.div
            variants={slowFadeUp}
            className="min-w-0 w-full lg:flex-[1.1_1_0%] text-center"
          >
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold tracking-wide text-emerald-700">
              <span className="relative mr-2 inline-flex h-5 w-5 items-center justify-center">
                <span
                  className="soft-ping absolute inline-flex h-5 w-5 rounded-full bg-emerald-400 opacity-60"
                  aria-hidden="true"
                />
                <span
                  className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm motion-safe:animate-solution-tick-pulse"
                  aria-hidden="true"
                >
                  <Check className="h-3 w-3" strokeWidth={2.75} />
                </span>
              </span>
              The Solution
            </span>
            <h3 className="font-display mx-auto mt-4 max-w-3xl text-section font-bold text-secondary leading-tight [text-wrap:balance]">
              One Unified Platform
              <br />
              to Run Your{" "}
              <span className="bg-[linear-gradient(90deg,rgba(126,211,157,1)_0%,rgba(22,163,74,1)_58%)] bg-clip-text text-transparent">
                Entire Hospital
              </span>
            </h3>
            <p className="mx-auto mt-4 max-w-2xl text-muted leading-7">
              Our hospital information system connects clinical, operational,
              and financial workflows into one intelligent platform.
            </p>
          </motion.div>

          <motion.div
            variants={slowFadeUp}
            className="min-w-0 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 items-stretch"
          >
            {solutionCards.map((item, index) => {
              const dividerClass = [
                index > 0 && "border-t border-slate-200",
                index > 0 && index < 2 && "sm:border-t-0",
                index % 2 === 1 && "sm:border-l",
                index > 0 && "lg:border-l lg:border-t-0",
              ]
                .filter(Boolean)
                .join(" ");
              const accentText =
                item.accent.split(" ").find((c) => c.startsWith("text-")) ?? "text-emerald-600";
              return (
                <motion.article
                  variants={slowFadeUp}
                  key={item.id}
                  className={`relative flex h-full min-h-0 flex-row items-start gap-3 p-5 sm:flex-col sm:items-stretch sm:gap-0 sm:p-6 lg:px-8 ${dividerClass}`}
                >
                  <IconBox size="xl" tone={item.accent} className="mb-0 shrink-0 sm:mb-4">
                    {renderSolutionIcon(item)}
                  </IconBox>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-semibold text-secondary">
                      {item.lines[0]} {item.lines[1]}
                    </h4>
                    <ul className="mt-2 flex flex-1 flex-col space-y-2 sm:mt-4 sm:space-y-2.5">
                      {item.points.map((point) => (
                        <li key={point} className="flex items-start gap-2.5 text-base leading-relaxed text-muted">
                          <Check className={`mt-0.5 h-4 w-4 shrink-0 ${accentText}`} strokeWidth={2.25} />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
